import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Resolve backend URL robustly. In a properly-deployed environment
// `REACT_APP_BACKEND_URL` is baked in at build time. In rare cases (custom
// domain w/ same-origin routing, or a misconfigured build) it can be missing
// or the literal string "undefined" — both have caused ERR_NAME_NOT_RESOLVED
// in production. Falling back to "" makes axios issue relative-origin calls
// like `/api/me/usage`, which the Kubernetes/Nginx ingress routes to the
// backend on the same host. Always safer than emitting `undefined/api/...`.
const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = (!RAW_BACKEND_URL || RAW_BACKEND_URL === 'undefined') ? '' : RAW_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Stable axios instance — memoize so consumers' useEffect([api]) doesn't fire
  // on every AuthProvider render, which previously caused infinite refetch loops
  // in PostsPage/CalendarPage/ConnectionsPage/ClientsPage/useClients hook.
  const api = useMemo(() => axios.create({ baseURL: API }), []);

  // Keep the latest token in a ref so the request interceptor always reads
  // the current value without forcing the api instance to be re-created.
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // Wire request + response interceptors exactly once. Auth header is applied
  // dynamically via the ref so changing token never invalidates `api`.
  useEffect(() => {
    const reqId = api.interceptors.request.use((cfg) => {
      const t = tokenRef.current;
      if (t) cfg.headers.Authorization = `Bearer ${t}`;
      else delete cfg.headers.Authorization;
      return cfg;
    });
    // Global 402 → upgrade modal. Any backend response with HTTP 402 (Payment Required)
    // — image quota exhausted, posts quota exhausted, trial expired — automatically
    // surfaces the friendly upgrade modal. Components keep their own try/catch but
    // don't have to render their own upgrade UI.
    const resId = api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Normalize Pydantic 422 validation errors. FastAPI returns
        //   detail: [{ type, loc, msg, input, url }, ...]
        // — passing that array (or an object) into `toast.error(...)` or
        // any JSX text node crashes React with "Objects are not valid as
        // a React child" (minified #31). Convert to a readable string here
        // once so every consumer can safely render `e.response.data.detail`.
        const d = error?.response?.data?.detail;
        if (Array.isArray(d)) {
          error.response.data.detail = d
            .map((it) => (typeof it === 'string' ? it : it?.msg || JSON.stringify(it)))
            .join('; ');
        } else if (d && typeof d === 'object') {
          error.response.data.detail = d.msg || JSON.stringify(d);
        }
        if (error?.response?.status === 402) {
          window.dispatchEvent(
            new CustomEvent('show-upgrade-modal', {
              detail: { reason: error.response?.data?.detail || '' },
            })
          );
        }
        return Promise.reject(error);
      }
    );
    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
    };
  }, [api]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, api]);

  useEffect(() => {
    // If returning from the Google OAuth redirect, the GoogleAuthCallback
    // handler will exchange the session_id and set the token — skip the /me
    // check here to avoid a race that would bounce the user to /login.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser]);

  // Capture affiliate referral (?ref=CODE) and record it once the user is known.
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref) {
        const code = ref.trim();
        localStorage.setItem('affiliate_ref', code);
        // Count the click once per browser session for this code (dedupe re-renders).
        const key = `aff_click_${code}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          const base = process.env.REACT_APP_BACKEND_URL;
          fetch(`${base}/api/affiliate/click`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code }),
          }).catch(() => {});
        }
      }
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    const ref = (() => { try { return localStorage.getItem('affiliate_ref'); } catch (_) { return null; } })();
    if (user && ref) {
      api.post('/affiliate/track', { code: ref })
        .catch(() => {})
        .finally(() => { try { localStorage.removeItem('affiliate_ref'); } catch (_) { /* noop */ } });
    }
  }, [user, api]);

  /**
   * Login can resolve in two shapes:
   *  1. Normal: { access_token, user }   → caller treats as success
   *  2. 2FA:    { requires_2fa: true, twofa_method, mfa_token, masked_email }
   *             → caller routes to /2fa-challenge with mfa_token
   * Returns the raw response.data so the LoginPage can branch on it.
   */
  const login = async (email, password, captchaToken) => {
    const response = await api.post('/auth/login', { email, password, captcha_token: captchaToken });
    if (response.data?.requires_2fa) {
      // Caller is responsible for routing to challenge screen — don't set user yet
      return response.data;
    }
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return response.data;
  };

  /** Exchange (mfa_token + code) for a real session. */
  const verify2fa = async (mfaToken, code) => {
    const response = await api.post('/auth/2fa/verify', { mfa_token: mfaToken, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, captchaToken, pendingPlan = null) => {
    const response = await api.post('/auth/register', {
      email, password, name,
      captcha_token: captchaToken,
      pending_plan: pendingPlan || null,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  /** Apply a session obtained out-of-band (e.g. Google OAuth callback). */
  const applySession = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_stash_token');
    localStorage.removeItem('admin_stash_meta');
    setToken(null);
    setUser(null);
  };

  /**
   * Admin impersonation — log in as another user without their password.
   * Stashes the admin's current token in localStorage under `admin_stash_token`
   * so `stopImpersonation()` can restore the session round-trip-free.
   * `meta` is JSON with the admin + target identity for the banner.
   */
  const impersonate = async (targetUserId) => {
    const response = await api.post(`/admin/users/${targetUserId}/impersonate`);
    const { access_token, user: targetUser, impersonator } = response.data;
    // Stash the current admin token + identity so we can swap back
    if (token) {
      localStorage.setItem('admin_stash_token', token);
      localStorage.setItem(
        'admin_stash_meta',
        JSON.stringify({
          admin_id: impersonator.id,
          admin_email: impersonator.email,
          admin_name: impersonator.name,
          target_id: targetUser.id,
          target_email: targetUser.email,
          target_name: targetUser.name,
          started_at: new Date().toISOString(),
        })
      );
    }
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(targetUser);
    return targetUser;
  };

  /** Restore the admin session that triggered the active impersonation. */
  const stopImpersonation = () => {
    const stashed = localStorage.getItem('admin_stash_token');
    if (!stashed) return false;
    localStorage.setItem('token', stashed);
    localStorage.removeItem('admin_stash_token');
    localStorage.removeItem('admin_stash_meta');
    setToken(stashed);
    // user will be refetched by the token-change effect (fetchUser)
    return true;
  };

  /** Read-only — used by the banner to know if we're impersonating. */
  const getImpersonationMeta = () => {
    try {
      const raw = localStorage.getItem('admin_stash_meta');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  /** Rotate the in-memory + stored JWT after a successful change-password.
   *  Returns the new user object so the caller can show it. */
  const changePassword = async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  /** Re-fetch /auth/me — useful after email verification or 2FA changes. */
  const refreshUser = async () => {
    if (!token) return null;
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (e) {
      return null;
    }
  };

  const ctxValue = useMemo(
    () => ({
      user, token, loading, login, register, logout, api,
      verify2fa, refreshUser, changePassword, applySession,
      impersonate, stopImpersonation, getImpersonationMeta,
    }),
    // login/register/logout/verify2fa/refreshUser close over `token`/`api` so they
    // need to be re-created when those change (otherwise stale closures). Keep
    // them out of deps and only memoize the wrapper for state-driven changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, token, loading, api]
  );

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
